import { HttpClient } from '@adapters/HttpClient';
import {
  CourierFileFilters,
  CourierFilesResponse,
  ExportCourierFilesRequest,
  ExportCourierFilesResponse,
  MarkProcessedRequest,
  MarkProcessedResponse,
  BulkInboxCourierFilesRequest,
  BulkInboxCourierFilesResponse,
  InboxRunCourierFilesRequest,
  InboxRunCourierFilesResponse,
  UploadCourierFileRequest,
  UploadCourierFileResponse,
} from '../models';

export class CourierFilesApiAdapter {
  constructor(private httpClient: HttpClient) {}

  /**
   * Obtiene listado de archivos de courier con filtros y paginación
   * GET /api/v1/courier-cod-files
   *
   * Query Parameters:
   * - per_page: Registros por página (default: 15)
   * - page: Número de página (default: 1)
   * - sorting[column]: Campo para ordenar
   * - sorting[direction]: asc o desc
   *
   * Advanced Filters:
   * - status: equals
   * - courier_id: in
   * - file_date: greater_equal / less_equal
   * - file_name: contains (búsqueda)
   */
  async getCourierFiles(filters: CourierFileFilters = {}): Promise<CourierFilesResponse> {
    const {
      courier_id,
      status,
      date_from,
      date_to,
      search,
      per_page = 15,
      page = 1,
      sort_by,
      sort_direction = 'desc',
    } = filters;

    const params: Record<string, any> = {
      page,
      per_page,
      ...(sort_by ? { 'sorting[column]': sort_by } : {}),
      ...(sort_direction ? { 'sorting[direction]': sort_direction } : {}),
    };

    // 🎯 Construir advanced_filters dinámicamente
    let filterIndex = 0;

    // Filtro por status
    if (status) {
      params[`advanced_filters[filters][${filterIndex}][column]`] = 'status';
      params[`advanced_filters[filters][${filterIndex}][operator]`] = 'equals';
      params[`advanced_filters[filters][${filterIndex}][value]`] = status;
      filterIndex++;
    }

    // Filtro por courier_id
    if (courier_id) {
      params[`advanced_filters[filters][${filterIndex}][column]`] = 'courier_id';
      params[`advanced_filters[filters][${filterIndex}][operator]`] = 'in';
      params[`advanced_filters[filters][${filterIndex}][value][0]`] = courier_id;
      filterIndex++;
    }

    // Filtro por fecha desde (file_date >= date_from)
    if (date_from) {
      params[`advanced_filters[filters][${filterIndex}][column]`] = 'file_date';
      params[`advanced_filters[filters][${filterIndex}][operator]`] = 'greater_equal';
      params[`advanced_filters[filters][${filterIndex}][value]`] = date_from;
      filterIndex++;
    }

    // Filtro por fecha hasta (file_date <= date_to)
    if (date_to) {
      params[`advanced_filters[filters][${filterIndex}][column]`] = 'file_date';
      params[`advanced_filters[filters][${filterIndex}][operator]`] = 'less_equal';
      params[`advanced_filters[filters][${filterIndex}][value]`] = date_to;
      filterIndex++;
    }

    // Búsqueda por file_name (contains)
    if (search) {
      params[`advanced_filters[filters][${filterIndex}][column]`] = 'file_name';
      params[`advanced_filters[filters][${filterIndex}][operator]`] = 'contains';
      params[`advanced_filters[filters][${filterIndex}][value]`] = search;
      filterIndex++;
    }

    // Agregar logic si hay filtros
    if (filterIndex > 0) {
      params['advanced_filters[logic]'] = 'AND';
    }

    const response = await this.httpClient.get<CourierFilesResponse>('/courier-cod-files', params, {}, page === 1);

    return response;
  }

  /**
   * Sube y procesa un archivo de courier
   * POST /api/v1/cod-payments/courier-files/upload
   * Content-Type: multipart/form-data
   */
  async uploadCourierFile(data: UploadCourierFileRequest): Promise<UploadCourierFileResponse> {
    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('courier_id', String(data.courier_id));
    formData.append('file_date', data.file_date); // Formato Y-m-d (YYYY-MM-DD)

    const response = await this.httpClient.post<UploadCourierFileResponse>(
      '/cod-payments/courier-files/upload',
      formData,
      true,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    );

    return response;
  }

  /**
   * Carga masiva a la bandeja de entrada (received); el procesamiento lo hace un job posterior.
   * POST /api/v1/cod-payments/courier-files/inbox/bulk
   */
  async uploadCourierFilesBulk(data: BulkInboxCourierFilesRequest): Promise<BulkInboxCourierFilesResponse> {
    const formData = new FormData();
    formData.append('courier_id', String(data.courier_id));
    data.files.forEach((file) => {
      formData.append('files[]', file);
    });

    return this.httpClient.post<BulkInboxCourierFilesResponse>(
      '/cod-payments/courier-files/inbox/bulk',
      formData,
      true,
    );
  }

  /**
   * Ejecuta el job de procesamiento de la bandeja de inbox para un team (y opcionalmente un courier).
   * POST /api/v1/cod-payments/courier-files/inbox/run
   * Body JSON: { team_id, courier_id? }
   */
  async runInboxCourierFiles(data: InboxRunCourierFilesRequest): Promise<InboxRunCourierFilesResponse> {
    return this.httpClient.post<InboxRunCourierFilesResponse>('/cod-payments/courier-files/inbox/run', data);
  }

  /**
   * Marca uno o varios archivos como procesados
   * PUT /api/v1/courier-cod-files/mark-processed
   */
  async markProcessed(data: MarkProcessedRequest): Promise<MarkProcessedResponse> {
    const response = await this.httpClient.put<MarkProcessedResponse>('/courier-cod-files/mark-processed', data);

    return response;
  }

  /**
   * Exporta archivos de courier a Excel
   * POST /api/v1/courier-cod-files/export
   */
  async exportCourierFiles(
    data: ExportCourierFilesRequest,
    sorting?: { column: string; direction: 'asc' | 'desc' },
  ): Promise<ExportCourierFilesResponse> {
    // Construir query params para sorting
    const queryParams: Record<string, string> = {};
    if (sorting) {
      queryParams['sorting[column]'] = sorting.column;
      queryParams['sorting[direction]'] = sorting.direction;
    }

    // Construir URL con query params
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `/courier-cod-files/export${queryString ? `?${queryString}` : ''}`;

    const response = await this.httpClient.post<ExportCourierFilesResponse>(url, data);

    return response;
  }
}
